import { favorites, FAVORITE_WIDGET_SELECTOR } from '@/lib/favorites.js';

const guestClickBound = new WeakSet();
const WIDGET_RENDER_CHECK_MS = 1200;
const WIDGET_RENDER_MAX_ATTEMPTS = 3;
const widgetRenderRetries = new WeakMap();

/**
 * Keeps guest PDP URLs aligned with the selected colorway handle.
 * @param {string} handle
 * @returns {boolean} Whether the pathname changed
 */
const syncBrowserProductPath = handle => {
  if (!handle) return false;

  const { pathname, search, hash } = window.location;
  // Only re-align the handle on an actual PDP path. Guest color switches on non-PDP surfaces
  // (closet/outfit-picker, mini-PDP, collection, or page templates) must not rewrite the page URL.
  if (!pathname.includes('/products/')) return false;
  const nextPathname = pathname.replace(/\/products\/[^/?#]+/, `/products/${handle}`);

  if (nextPathname === pathname) return false;

  window.history.replaceState(window.history.state, '', `${nextPathname}${search}${hash}`);

  const canonical = document.querySelector('link[rel="canonical"]');
  if (canonical) canonical.setAttribute('href', `${window.location.origin}${nextPathname}`);

  return true;
};

/**
 * @param {HTMLElement} container
 * @param {string} productId
 * @param {string} handle
 * @param {string} productUrl
 * @param {string} [imageUrl]
 */
const syncContainerAttributes = (container, productId, handle, productUrl, imageUrl = '') => {
  container.setAttribute('data-fav-pid', productId);

  if (handle) {
    container.setAttribute('data-fav-handle', handle);
    container.setAttribute('data-fav-url', productUrl);
  }

  if (imageUrl) container.setAttribute('data-fav-image', imageUrl);

  const slot = container.querySelector('.klaviyo-wishlist-slot');
  if (slot) {
    slot.setAttribute('data-product-id', productId);
    if (productUrl) slot.setAttribute('data-product-url', productUrl);
  }
};

/**
 * @param {HTMLElement} container
 * @param {string} productId
 * @param {string} productUrl
 * @param {boolean} [syncRenderedId]
 */
const applyWidgetProductAttributes = (container, productId, productUrl, syncRenderedId = false) => {
  container.querySelectorAll('.klaviyo-wishlist-slot, .kl-hub-favorites-widget, [data-product-id]').forEach(el => {
    el.setAttribute('data-product-id', productId);
    if (productUrl) el.setAttribute('data-product-url', productUrl);
    if (syncRenderedId) el.setAttribute('data-rendered-product-id', productId);
  });
};

/**
 * @param {HTMLElement} container
 * @param {string} productId
 * @param {string} handle
 * @param {string} productUrl
 * @param {string} [imageUrl]
 * @param {boolean} [syncRenderedId]
 */
const syncSlotAttributes = (container, productId, handle, productUrl, imageUrl = '', syncRenderedId = false) => {
  syncContainerAttributes(container, productId, handle, productUrl, imageUrl);
  if (syncRenderedId) applyWidgetProductAttributes(container, productId, productUrl, true);
};

/**
 * @param {HTMLElement} container
 * @param {string} productId
 * @param {string} productUrl
 */
const verifyWidgetRendered = (container, productId, productUrl) => {
  setTimeout(() => {
    if (container.getAttribute('data-fav-mounted-pid') !== productId) return;
    if (container.querySelector(FAVORITE_WIDGET_SELECTOR)) {
      widgetRenderRetries.delete(container);
      return;
    }

    const state = widgetRenderRetries.get(container);
    const attempts = state && state.pid === productId ? state.attempts : 0;
    if (attempts >= WIDGET_RENDER_MAX_ATTEMPTS) return;

    widgetRenderRetries.set(container, { pid: productId, attempts: attempts + 1 });
    container.replaceChildren(favorites.createWishlistSlot(productId, productUrl));
    favorites.invokeHydrateProduct(container.getAttribute('data-fav-handle') || '');
    verifyWidgetRendered(container, productId, productUrl);
  }, WIDGET_RENDER_CHECK_MS);
};

/**
 * Replaces the wishlist slot so Klaviyo re-binds for guest shoppers on color switch.
 * @param {HTMLElement} container
 * @param {string} productId
 * @param {string} productUrl
 */
const remountGuestSlot = (container, productId, productUrl) => {
  if (favorites.isKlaviyoHubOpen()) {
    container.setAttribute('data-fav-mounted-pid', productId);
    return;
  }

  const hasWidget = !!container.querySelector(FAVORITE_WIDGET_SELECTOR);
  if (hasWidget) {
    container.setAttribute('data-fav-mounted-pid', productId);
    return;
  }

  const mountedPid = container.getAttribute('data-fav-mounted-pid');
  const slot = favorites.getFavoriteSlot(container);
  const slotPid = favorites.normalizeId(slot?.getAttribute('data-product-id'));

  if (slotPid === productId) {
    container.setAttribute('data-fav-mounted-pid', productId);
    verifyWidgetRendered(container, productId, productUrl);
    return;
  }

  if (mountedPid === productId) {
    verifyWidgetRendered(container, productId, productUrl);
    return;
  }

  container.replaceChildren(favorites.createWishlistSlot(productId, productUrl));
  container.setAttribute('data-fav-mounted-pid', productId);
  verifyWidgetRendered(container, productId, productUrl);
};

/**
 * @param {HTMLElement} container
 * @param {string} productId
 * @param {string} handle
 * @param {string} productUrl
 * @param {string} [productName]
 * @param {string} [imageUrl]
 */
const syncFavoriteContext = (container, productId, handle, productUrl, productName = '', imageUrl = '') => {
  const resolvedImage = favorites.toAbsoluteUrl(imageUrl);
  syncSlotAttributes(container, productId, handle, productUrl, resolvedImage);
  favorites.setActiveProduct(productId, productUrl, resolvedImage, productName);
  if (productName) container.setAttribute('data-fav-name', productName);
  favorites.trackViewedProduct(productId, handle, productUrl, productName, resolvedImage);
  if (container.querySelector(FAVORITE_WIDGET_SELECTOR)) favorites.syncWidgetFavoriteVisual(container, productId);
};

/**
 * Re-syncs slot attrs and activeProduct immediately before Klaviyo handles the click.
 * @param {HTMLElement} container
 */
const resyncBeforeFavoriteClick = container => {
  const { productId, handle, productUrl, imageUrl, productName } = favorites.readFavoriteContext(container);
  if (!productId) return;

  syncSlotAttributes(container, productId, handle, productUrl, imageUrl, true);
  favorites.setActiveProduct(productId, productUrl, imageUrl, productName);

  const activeVariants = window.customerHub?.activeProduct?.variants;
  if (Array.isArray(activeVariants) && activeVariants.length) {
    window.customerHub.activeProduct.variants = favorites.orderSelectedVariantFirst(activeVariants);
  }

  favorites.invokeHydrateProduct(handle);
  favorites.hydrateActiveProductFromHandle(productId, handle, favorites.currentHydrateGeneration(), imageUrl, productName);
};

/**
 * @param {HTMLElement} container
 */
const bindGuestClickHandler = container => {
  if (guestClickBound.has(container)) return;

  guestClickBound.add(container);
  container.addEventListener('pointerdown', () => resyncBeforeFavoriteClick(container), true);
};

/**
 * Syncs PDP favorite widgets to the selected colorway product.
 * @param {string|number} productId
 * @param {string} handle
 * @param {string} [productName]
 * @param {boolean} [updateUrl]
 * @param {string|Array} [imageOrImages]
 */
export const syncPdpFavoriteProduct = (productId, handle, productName = '', updateUrl = true, imageOrImages = '') => {
  const normalizedId = favorites.normalizeId(productId);
  if (!normalizedId || !handle) return;

  const generation = favorites.nextHydrateGeneration();
  const productUrl = favorites.buildProductUrl(handle);
  const imageUrl = favorites.resolveFeaturedImageUrl(imageOrImages);
  const guest = favorites.isGuestShopper();

  if (guest && updateUrl && !favorites.isKlaviyoHubOpen()) syncBrowserProductPath(handle);

  document.querySelectorAll('[data-pdp-favorite]').forEach(container => {
    if (guest) remountGuestSlot(container, normalizedId, productUrl);
    bindGuestClickHandler(container);
    syncFavoriteContext(container, normalizedId, handle, productUrl, productName, imageUrl);
  });

  favorites.hydrateActiveProductFromHandle(normalizedId, handle, generation, imageUrl, productName);
};
